import { MAX_IN_MEMORY_DOWNLOAD } from "../config";
import { createServiceWorkerSink, serviceWorkerSinkAvailable } from "./swsink";

export type SinkKind = "filesystem" | "serviceworker" | "memory";

export interface Sink {
  kind: SinkKind;
  /** Consume the decrypted stream and deliver the file to the user. */
  write: (stream: ReadableStream<Uint8Array>) => Promise<void>;
  /** Called when decryption fails midway; removes partial output where possible. */
  abort: () => Promise<void>;
}

interface SaveFilePickerOptions {
  suggestedName?: string;
}

interface FileSystemWritableFileStreamLike extends WritableStream<Uint8Array> {
  truncate?: (size: number) => Promise<void>;
}

interface FileSystemFileHandleLike {
  createWritable: () => Promise<FileSystemWritableFileStreamLike>;
}

type ShowSaveFilePicker = (opts?: SaveFilePickerOptions) => Promise<FileSystemFileHandleLike>;

export function hasFileSystemAccess(): boolean {
  return typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker === "function";
}

/**
 * Picks the best available sink (§9 step 4). MUST be called synchronously inside the user's click
 * handler (before any other await) so the save dialog is allowed to open.
 *
 * 1. Chromium desktop: File System Access API → decrypted bytes stream straight to disk.
 * 2. Firefox / Safari / Android: Service Worker streaming response (public/sw.js) → native download.
 * 3. Last resort: in-memory Blob, capped by MAX_IN_MEMORY_DOWNLOAD.
 */
export async function chooseSink(fileName: string, size: number): Promise<Sink> {
  if (hasFileSystemAccess()) {
    const picker = (window as unknown as { showSaveFilePicker: ShowSaveFilePicker }).showSaveFilePicker;
    const handle = await picker({ suggestedName: fileName });
    const writable = await handle.createWritable();
    return {
      kind: "filesystem",
      write: (stream) => stream.pipeTo(writable),
      abort: async () => {
        try {
          await writable.abort();
        } catch {
          /* already closed */
        }
      },
    };
  }

  if (await serviceWorkerSinkAvailable()) {
    try {
      return await createServiceWorkerSink(fileName, size);
    } catch {
      /* fall through to memory for small files */
    }
  }

  if (size > MAX_IN_MEMORY_DOWNLOAD) {
    throw new Error(
      `This browser cannot stream a ${(size / 1024 ** 3).toFixed(1)} GB file to disk. Use Chrome, Edge or Firefox on desktop for files over ${MAX_IN_MEMORY_DOWNLOAD / 1024 ** 2} MB.`,
    );
  }

  return {
    kind: "memory",
    write: async (stream) => {
      const blob = await new Response(stream).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    abort: async () => undefined,
  };
}
